import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getCurrentLanguage } from '../utils/translations';
import api from '../components/api';
import { FullPageLoader } from '../components/Loader';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import ArticleHero from '../components/ArticleHero';
import ArticleContent from '../components/ArticleContent';
import ArticleActions from '../components/ArticleActions';
import CommentsSection from '../components/CommentsSection';

const ActualiteDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Fonction pour traduire le contenu de l'article
  const translateArticleContent = (article, field) => {
    if (!article) return '';
    
    const lang = currentLanguage;
    
    if (field === 'title') {
      if (lang === 'en' && article.title_en) {
        return article.title_en;
      }
      return article.title_fr || article.title || '';
    }
    
    if (field === 'description') {
      if (lang === 'en' && article.description_en) {
        return article.description_en;
      }
      return article.description_fr || article.description || '';
    }
    
    return article[field] || '';
  };

  // Fonction pour récupérer l'article par slug
  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/articles/slug/${slug}/`);
      setArticle(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la récupération de l\'article:', err);
      setError('Article non trouvé');
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer les commentaires
  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await api.get(`/api/articles/${article.id}/comments/`);
      console.log('Commentaires récupérés:', response.data);
      setComments(response.data);
    } catch (err) {
      console.error('Erreur lors de la récupération des commentaires:', err);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Récupérer l'article au chargement
  useEffect(() => {
    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  // Récupérer les commentaires quand l'article est chargé
  useEffect(() => {
    if (article) {
      console.log('Article chargé, ID:', article.id);
      fetchComments();
    }
  }, [article]);

  if (loading) {
    return (
      <>
        <Navbar />
        <FullPageLoader />
        <Footer />
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="h-16 w-16 text-red-500 mx-auto mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Article non trouvé</h2>
            <p className="text-gray-600 mb-6">L'article que vous recherchez n'existe pas ou a été supprimé.</p>
            <Link
              to={`/${currentLanguage}/actualites`}
              className="inline-flex items-center bg-[#089556] hover:bg-[#07D1EB] text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux actualités
            </Link>
          </div>
        </div>
      </>
    );
  }

  const translatedTitle = translateArticleContent(article, 'title');
  const translatedDescription = translateArticleContent(article, 'description');
  const translatedCategory = article.category_info ? 
    (currentLanguage === 'en' ? article.category_info.libelle_en : article.category_info.libelle_fr) : 
    'Article';

  return (
    <>
      <Navbar />
      
      <ArticleHero
        title={translatedTitle}
      />

      <ArticleContent
        coverImage={article.cover_image_url}
        title={translatedTitle}
        description={translatedDescription}
      />

      <ArticleActions />

      <CommentsSection
        comments={comments}
        setComments={setComments}
        articleId={article.id}
        commentsLoading={commentsLoading}
      />

      <Footer />
    </>
  );
};

export default ActualiteDetail;